import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import TestimonialSlider from '@/components/TestimonialSlider';
import { Search, Database, FileSearch, AlertTriangle, CheckCircle, Shield, ChevronDown, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TrademarkSearch = () => {
  const [showPopup, setShowPopup] = useState(false);

  // FAQ Component (matching homepage design)
  const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
        >
          <span className="font-semibold text-lg pr-4" style={{ color: 'var(--text-dark)' }}>
            {question}
          </span>
          <ChevronDown 
            className={`text-indigo-600 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
            size={24} 
          />
        </button>
        <div 
          className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
        >
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-gray-600 leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    );
  };
  
  const searchTypes = [
    {
      icon: Search,
      title: 'Basic Search',
      description: 'Quick preliminary search to identify direct matches in the USPTO database.',
    },
    {
      icon: Database,
      title: 'Comprehensive Search',
      description: 'Thorough search including federal, state, and common law trademarks.',
    },
    {
      icon: FileSearch,
      title: 'International Search',
      description: 'Global trademark search across multiple jurisdictions and databases.',
    },
  ];

  const benefits = [
    {
      icon: AlertTriangle,
      title: 'Avoid Conflicts',
      description: 'Identify potential conflicts before investing in your brand.',
    },
    {
      icon: CheckCircle,
      title: 'Increase Success',
      description: 'Higher approval rates with pre-filing comprehensive searches.',
    },
    {
      icon: Shield,
      title: 'Save Money',
      description: 'Avoid costly rejections and legal disputes down the road.',
    },
  ];

  const faqs = [
    {
      question: 'Why do I need a trademark search?',
      answer: 'A trademark search helps identify existing marks that may conflict with yours, reducing the risk of rejection and potential legal issues. It saves time and money by addressing concerns before filing.',
    },
    {
      question: 'What does a trademark search include?',
      answer: 'Our comprehensive search includes USPTO federal registrations, pending applications, state registrations, common law uses, domain names, and business directories.',
    },
    {
      question: 'How long does a trademark search take?',
      answer: 'Basic searches can be completed in 1-2 business days, while comprehensive searches typically take 3-5 business days to ensure thoroughness.',
    },
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section - Consistent Design */}
      <section className="relative min-h-[70vh] flex items-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-hidden" data-testid="search-hero">
        <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        
        <div className="container mx-auto px-4 py-12 md:py-16 lg:py-20 relative z-10">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-full mb-4">
                Research Service
              </div>
              <h1 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl lg:text-4xl sm:text-3xl sm:text-4xl lg:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Trademark Search
                </span>
                <br />
                <span style={{ color: 'var(--text-dark)' }}>Services</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Conduct comprehensive trademark searches before filing to ensure your mark is available and reduce rejection risks.
              </p>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1568992687947-868a62a9f521" alt="Research and analysis" className="w-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Types Section */}
      <section className="section-padding bg-white" data-testid="search-types-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Search Options</span>
            <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Choose Your Search Level
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional trademark searches tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {searchTypes.map((search, index) => {
              const Icon = search.icon;
              return (
                <div key={index} className="group" data-testid={`search-type-${index}`}>
                  <div className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-indigo-200 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 h-full">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-dark)' }}>
                      {search.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{search.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="benefits-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Benefits</span>
            <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Why Search First?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center group" data-testid={`benefit-${index}`}>
                  <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all border-2 border-gray-100 group-hover:border-indigo-200">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-dark)' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Informative Section 1 - Content Left, Image Right */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left - Content */}
            <div className="lg:w-1/2 space-y-6 order-2 lg:order-1 w-full">
              <div className="inline-block px-4 py-2 bg-amber-100 rounded-full mb-2">
                <span className="text-amber-600 font-semibold text-sm uppercase tracking-wide">Prevent Conflicts</span>
              </div>
              <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: 'var(--text-dark)' }}>
                Avoid Costly <span className="text-amber-600">Trademark Conflicts</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A comprehensive trademark search is your first line of defense against legal disputes, rejection, and wasted investment. Before you file, know exactly what exists and identify potential conflicts that could derail your application.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-amber-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Search federal, state, and common law databases</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-amber-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Identify confusingly similar existing marks</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-amber-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Increase your trademark approval chances</span>
                </li>
              </ul>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group mt-4"
              >
                Start Your Search
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>

            {/* Right - Image */}
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-amber-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-orange-200 rounded-full blur-3xl opacity-60"></div>
                <img 
                  src="https://images.unsplash.com/photo-1653038417404-1ae1f38c373e" 
                  alt="Research Typewriter" 
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 2 - Image Left, Content Right */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            {/* Right - Content */}
            <div className="lg:w-1/2 space-y-6 order-2 lg:order-1 w-full">
              <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-2">
                <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Thorough Analysis</span>
              </div>
              <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: 'var(--text-dark)' }}>
                More Than Just a <span className="text-indigo-600">Database Search</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our comprehensive trademark search goes beyond basic USPTO queries. We analyze phonetic similarities, visual resemblances, and conceptual overlaps to identify potential conflicts that automated searches miss.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With detailed reports and expert analysis, you'll understand exactly what risks exist and receive strategic recommendations for moving forward with your trademark application.
              </p>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group mt-4"
              >
                Get Expert Analysis
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>

            {/* Left - Image */}
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-indigo-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                <img 
                  src="https://images.unsplash.com/photo-1721046013656-0a0980264689" 
                  alt="Library Database" 
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Homepage Style */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left side - FAQs */}
            <div className="lg:w-1/2">
              <div className="mb-8">
                <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-4">
                  <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">FAQs</span>
                </div>
                <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: 'var(--text-dark)' }}>
                  Trademark Search FAQs
                </h2>
                <p className="text-lg text-gray-600">
                  Common questions about comprehensive trademark searches and conflict identification.
                </p>
              </div>

              {/* FAQ Accordion */}
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>

            {/* Right side - Image (same as homepage) */}
            <div className="lg:w-1/2 flex items-start">
              <div className="relative w-full sticky top-8">
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-indigo-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                <img 
                  src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740" 
                  alt="Business Consultation Meeting" 
                  className="relative rounded-3xl shadow-2xl w-full h-full object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Testimonials</span>
            <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Client Testimonials
            </h2>
          </div>
          <TestimonialSlider />
        </div>
      </section>

      <Footer />
      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} source="trademark-search" />}
    </div>
  );
};

export default TrademarkSearch;